import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Eye, ArrowLeft, Edit2, Check, Share2 } from 'lucide-react';
import { getExamSubmissionDetails } from './api';
import { generatePresignedUrl } from "../postFileAws";
import { postAws } from '../postAws';

export default function ViewExamResponse() {
  const { examId, submissionId } = useParams();
  const navigate = useNavigate();
  const [examSubmission, setExamSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnswer, setShowAnswer] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const [isEditing, setIsEditing] = useState({});
  const [editableMarks, setEditableMarks] = useState({});
  const [editableRemarks, setEditableRemarks] = useState({});

  useEffect(() => {
    const fetchExamSubmission = async () => {
      try {
        const response = await getExamSubmissionDetails(submissionId);
        const submissionData = JSON.parse(response.body).data;
        setExamSubmission(submissionData);

        // Initialize editable marks and remarks
        const initialMarks = {};
        const initialRemarks = {};
        submissionData.examQuestion.forEach(section => {
          section.questions.forEach(question => {
            initialMarks[question._id] = question.obtainedMarks || 0;
            initialRemarks[question._id] = question.remarks || '';
          });
        });
        setEditableMarks(initialMarks);
        setEditableRemarks(initialRemarks);

        // Generate presigned URLs for images
        const urls = {};
        for (const section of submissionData.examQuestion) {
          for (const question of section.questions) {
            if (question.image) {
              try {
                const url = await generatePresignedUrl(question.image);
                urls[question._id] = url;
              } catch (error) {
                console.error('Error generating presigned URL:', error);
              }
            }
          }
        }
        setImageUrls(urls);
      } catch (err) {
        console.error('Error fetching exam submission:', err);
      } finally {
        setLoading(false);
      }
    };

    if (submissionId) {
      fetchExamSubmission();
    }
  }, [submissionId]);

  const toggleAnswerView = (questionId) => {
    setShowAnswer(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const handleEdit = (questionId) => {
    setIsEditing(prev => ({ ...prev, [questionId]: true }));
  };

  const handleSave = (questionId) => {
    setIsEditing(prev => ({ ...prev, [questionId]: false }));
    setExamSubmission(prev => ({
      ...prev,
      examQuestion: prev.examQuestion.map(section => ({
        ...section,
        questions: section.questions.map(q =>
          q._id === questionId
            ? { ...q, obtainedMarks: editableMarks[questionId], remarks: editableRemarks[questionId] }
            : q
        )
      }))
    }));
  };

  const handleMarksChange = (questionId, value) => {
    const numValue = Math.min(Math.max(0, Number(value)), examSubmission.examQuestion.find(section => 
      section.questions.some(q => q._id === questionId)
    ).questions.find(q => q._id === questionId).marks);
    setEditableMarks(prev => ({ ...prev, [questionId]: numValue }));
  };

  const handleRemarksChange = (questionId, value) => {
    setEditableRemarks(prev => ({ ...prev, [questionId]: value }));
  };

  const handleShareResults = async () => {
    try {
      const updatedSubmission = {
        ...examSubmission,
        examQuestion: examSubmission.examQuestion.map(section => ({
          ...section,
          questions: section.questions.map(q => ({
            ...q,
            obtainedMarks: editableMarks[q._id],
            remarks: editableRemarks[q._id]
          }))
        }))
      };

      const response = await postAws('https://api-dev.aurait.ai/exam/update-evaluation', "POST", updatedSubmission);
      console.log("Response:", response);
      alert("Results shared successfully!");
      navigate(`/exam/details/${examId}`);
    } catch (error) {
      console.error("Error sharing results:", error);
      alert("Failed to share results. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-16 h-16 border-4 border-t-transparent border-r-transparent border-b-gray-300 border-l-blue-500 border-solid rounded-full animate-spin"></div>
        <p className='ml-2'>Loading exam submission...</p>
      </div> 
    );
  }

  if (!examSubmission) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>No exam submission data available.</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-100 to-purple-100 min-h-screen p-8">
      <div className="max-w-4xl mx-auto">
        <button 
          onClick={() => navigate(`/exam/details/${examId}`)}
          className="mb-4 flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Exam Details
        </button>
        <div className="bg-white shadow-lg rounded-lg p-6 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{examSubmission.exam_topic}</h1>
          <p className="text-xl text-gray-600 mb-4">Student: {examSubmission.studentName}</p>
          <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full inline-block mb-4 font-semibold">
            Exam Date: {new Date(examSubmission.examStartDate).toLocaleDateString()} - {new Date(examSubmission.examEndDate).toLocaleDateString()}
          </div>
          <div className="flex justify-between items-center">
            <div className="text-lg">
              Status: <span className="font-bold text-green-600">{examSubmission.status}</span>
            </div>
            <div className="text-lg">
              Total Score: <span className="font-bold text-blue-600">
                {examSubmission.examQuestion.reduce((total, section) => 
                  total + section.questions.reduce((sectionTotal, question) => 
                    sectionTotal + (editableMarks[question._id] || 0), 0
                  ), 0
                )}
                /{examSubmission.examQuestion.reduce((total, section) => 
                  total + section.questions.reduce((sectionTotal, question) => 
                    sectionTotal + question.marks, 0
                  ), 0
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {examSubmission.examQuestion.map((section, sectionIndex) => (
            <div key={section._id} className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Section {sectionIndex + 1}: {section.name}</h2>
              {section.questions.map((question, questionIndex) => (
                <div key={question._id} className="mb-6 border-t pt-4">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Question {questionIndex + 1}</h3>
                  <p className="text-gray-600 mb-4">{question.text}</p>
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => toggleAnswerView(question._id)}
                      className="flex items-center text-blue-500 hover:text-blue-600 transition duration-300 ease-in-out bg-blue-50 px-4 py-2 rounded-full"
                    >
                      <Eye className="w-5 h-5 mr-2" />
                      <span className="text-sm font-medium">
                        {showAnswer[question._id] ? 'Hide' : 'View'} Student's Answer
                      </span>
                    </button>
                    <div className="text-lg flex items-center">
                      Marks: <span className="font-bold text-blue-600 ml-2">{question.marks}</span>
                    </div>
                  </div>
                  {showAnswer[question._id] && (
                    <div className="mb-4">
                      {question.user_answer ? (
                        <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{question.user_answer}</p>
                      ) : question.image && imageUrls[question._id] ? (
                        <img 
                          src={imageUrls[question._id]} 
                          alt="Student's answer" 
                          className="mt-2 max-w-full h-auto rounded-lg shadow-sm"
                        />
                      ) : (
                        <p className="text-yellow-600 bg-yellow-50 p-4 rounded-lg">No answer submitted for this question.</p>
                      )}
                    </div>
                  )}
                  <div className="mt-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Evaluation:</h4>
                    <div className="bg-gray-50 p-4 rounded-lg mb-2">
                      {isEditing[question._id] ? (
                        <textarea
                          value={editableRemarks[question._id]}
                          onChange={(e) => handleRemarksChange(question._id, e.target.value)}
                          className="w-full p-2 border rounded"
                          rows={3}
                        />
                      ) : (
                        <p className="text-gray-700">{editableRemarks[question._id] || 'No remarks provided.'}</p>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-lg">
                        Marks Obtained: 
                        {isEditing[question._id] ? (
                          <input
                            type="number"
                            value={editableMarks[question._id]}
                            onChange={(e) => handleMarksChange(question._id, e.target.value)}
                            className="w-16 ml-2 p-1 border rounded"
                            min="0"
                            max={question.marks}
                          />
                        ) : (
                          <span className="font-bold text-blue-600 ml-2">{editableMarks[question._id] || 0}</span>
                        )}
                        <span className="ml-1">/ {question.marks}</span>
                      </p>
                      {isEditing[question._id] ? (
                        <button
                          onClick={() => handleSave(question._id)}
                          className="text-green-600 hover:text-green-700 transition-colors duration-200"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEdit(question._id)}
                          className="text-blue-600 hover:text-blue-700 transition-colors duration-200"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto mt-4">
                    <table className="min-w-full border-collapse shadow-sm rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-blue-500 text-white">
                          <th className="py-3 px-6 text-left text-sm font-semibold uppercase tracking-wider">Question Type</th>
                          <th className="py-3 px-6 text-left text-sm font-semibold uppercase tracking-wider">Marks</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        <tr className="hover:bg-blue-50 transition-colors duration-200">
                          <td className="py-4 px-6 border-b border-blue-100">{question.type}</td>
                          <td className="py-4 px-6 border-b border-blue-100">{question.marks}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-end">
          <button 
            onClick={handleShareResults}
            className="bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Results with Student
          </button>
        </div>
      </div>
    </div>
  );
}

